import React from "react";
import { useModalHeadTabs } from "../modal/Modal";
import styles from "./SectionTabs.module.scss";

// The tab band shared by tabbed section pages (Shop, Profile): OpenFront-style,
// edge to edge directly under the modal header's title row, text tabs in
// wide-spaced caps with a cyan underline on the active one. It lives IN the
// modal header (via useModalHeadTabs), so it never scrolls: on desktop the
// body scrolls beneath the header, on phones the whole header is sticky at
// the top of the page. The host modal carries .app-modal--tabbed so the header
// keeps no gap below its divider; see app.scss.
export interface SectionTab<Id extends string> {
  id: Id;
  label: string;
  // Something waits there (achievements to open, a new item): a small gold
  // count beside the label, and the tab glows gold until it is seen.
  badge?: number;
}

// Phone mode, matching the stylesheet's breakpoint (see app-responsive.scss):
// there the page scrolls, not the modal body.
const PHONE = "(max-width: 900px), (pointer: coarse)";

export default function SectionTabs<Id extends string>({ tabs, value, onChange, rewindOnChange = true }: {
  tabs: readonly SectionTab<Id>[];
  value: Id;
  onChange: (id: Id) => void;
  // Switching tabs also rewinds the scroll: the band is pinned chrome, so
  // landing mid-scroll in the next tab reads as a glitch.
  rewindOnChange?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  function rewind() {
    const modal = ref.current?.closest<HTMLElement>(".app-modal");
    if (!modal) return;
    // A landscape phone's full-screen section (body.section-app) scrolls its
    // own content area, like desktop.
    const app = document.body.classList.contains("section-app");
    if (!app && typeof matchMedia === "function" && matchMedia(PHONE).matches) {
      // Back up to the panel's top edge (the nav above it stays scrolled away).
      const panelTop = modal.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > panelTop) window.scrollTo({ top: panelTop });
    } else {
      modal.querySelector(".app-modal-body")?.scrollTo({ top: 0 });
    }
  }
  const band = (
    <div className={styles.tabs} role="tablist" ref={ref}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          className={`${styles.tab} ${value === t.id ? styles.tabActive : ""} ${t.badge ? styles.tabGlow : ""}`}
          onClick={() => {
            onChange(t.id);
            if (rewindOnChange) rewind();
          }}
        >
          {t.label}
          {t.badge ? <span className={styles.badge}>{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
  const inHead = useModalHeadTabs(band);
  return inHead ? null : band;
}
